import { Button, OutlinedInput } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import BackupIcon from '@material-ui/icons/Backup';
import NoteAddIcon from '@material-ui/icons/NoteAdd';
import { cloneDeep } from 'lodash-es';
import { useState } from 'react';
import React from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { MaterialSearchDropdownMulti } from '~/src/dash/components/material-search-dropdown-multi';
import { MenuButton } from '~/src/dash/components/menu-button';
import PillSwitcher from '~/src/dash/components/pill-switcher';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  IListViewTableColumnInfo,
  IListViewTableRow,
  Order,
} from '~/src/dash/components/tables/ListViewTable/common';
import { IWizardStepProps } from '~/src/dash/components/wizard';
import WizardButtons from '~/src/dash/components/wizard-buttons';
import { Col, Cols, Panel, Row, Rows, Text } from '~/src/dash/publicaUI';
import { getColorTheme } from '~/src/dash/resellers/utils/importResellerConfig';
import { IMenuList } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';
import { LoadingState } from '~/src/dash/utils/loading-states';
import { ssaiHouseAdsCfg } from '~/src/dash/views/networks/configs/config';

/**
 * All these props are state variables passed from
 * the parent component (network-settings-view)
 */
interface IHouseAdsUploadFormProps extends IWizardStepProps {
  isInWizard: boolean;
  uploadSSAIHouseAdMediafile: (
    event: React.ChangeEvent<HTMLInputElement>,
    customName: string,
  ) => void;
  uploadSSAIHouseAdByURL: (url: string, customName: string) => void;
  ssaiHouseAdsDropdownRows: IMenuList;
  handleUpdateSSAIHouseAdsDropdownAndTableRows: (rows: IMenuList) => void;
  ssaiHouseAdsTableRows: IListViewTableRow[];
  loadingState: LoadingState;
}
enum AssignHouseAdOptions {
  Existing = 'Use Existing House Ad',
  New = 'Upload House Ad',
}

enum UploadMethods {
  NoUpload,
  ExternalURL,
  LocalFile,
}

const HouseAdsUploadForm = ({
  onNext,
  onBack,
  isInWizard,
  uploadSSAIHouseAdMediafile,
  uploadSSAIHouseAdByURL,
  ssaiHouseAdsDropdownRows,
  handleUpdateSSAIHouseAdsDropdownAndTableRows,
  ssaiHouseAdsTableRows,
  loadingState,
}: IHouseAdsUploadFormProps) => {
  // Upload
  const [houseAdName, setHouseAdName] = useState('');
  const [externalURL, setExternalURL] = useState('');
  const [assignHouseAdOption, setAssignHouseAdOption] = useState(
    AssignHouseAdOptions.Existing.toString(),
  );
  const [selectedUploadMethod, setSelectedUploadMethod] = useState(UploadMethods.NoUpload);

  // Table
  const [order, setOrder] = useState<Order>(ssaiHouseAdsCfg.config.order || 'asc');
  const [orderBy, setOrderBy] = useState<string>(ssaiHouseAdsCfg.config.orderBy || 'Name');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ssaiHouseAdsCfg.config.rowsPerPage || 10);

  // Pill Switching
  const houseAdUploadPills = Object.values(AssignHouseAdOptions).map((opt) => {
    return {
      text: opt,
      activeTextColor: publicaColors.blue,
      activeBackgroundColor: publicaColors.iceWhite,
      icon: {
        active:
          opt === AssignHouseAdOptions.Existing
            ? `assets/shared/svg/ImageFocused.svg`
            : `assets/shared/svg/UploadFocused.svg`,
        inactive:
          opt === AssignHouseAdOptions.Existing
            ? `assets/shared/svg/ImageUnfocused.svg`
            : `assets/shared/svg/UploadUnfocused.svg`,
      },
    };
  });

  const handleUploadMethodPillChange = (pillText: string) => {
    unstable_batchedUpdates(() => {
      setAssignHouseAdOption(pillText);
      setSelectedUploadMethod(UploadMethods.NoUpload);
    });
  };

  // Table Controls

  // this sets up the changes to force a re-sort
  const handleOnClickColumnHeader = (column: IListViewTableColumnInfo) => {
    const { key, defaultOrderDirection } = column;
    if (orderBy !== key) {
      unstable_batchedUpdates(() => {
        setOrder(defaultOrderDirection || 'asc');
        setOrderBy(key);
        setPage(0);
      });
    } else {
      const isAsc = orderBy === key && order === 'asc';
      unstable_batchedUpdates(() => {
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
        setPage(0);
      });
    }
  };

  const handleChangePage = (_e: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    unstable_batchedUpdates(() => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    });
  };

  const displayedRows = (
    rows: IListViewTableRow[],
    currPage: number,
    rowsPerPage: number,
  ): IListViewTableRow[] => {
    const start = currPage * rowsPerPage;
    return rows.slice(start, Math.min(start + rowsPerPage, rows.length));
  };

  const deleteSSAIHouseAdTableRow = (id: string) => {
    const newRows = cloneDeep(ssaiHouseAdsDropdownRows);
    if (newRows[id]) {
      newRows[id].selected = false;
      handleUpdateSSAIHouseAdsDropdownAndTableRows(newRows);
    }
    return new Promise((resolve) => resolve(undefined));
  };

  // Upload Controls

  const AddButton = () => {
    return (
      <Button
        component='label'
        onClick={() => {
          setSelectedUploadMethod(UploadMethods.ExternalURL);
          uploadSSAIHouseAdByURL(externalURL, houseAdName);
        }}
        style={{
          color: publicaColors.blue,
        }}
      >
        <Text color={publicaColors.blue} bold>
          +UPLOAD
        </Text>
      </Button>
    );
  };

  const CreateFromAssetURLRow = () => {
    return (
      <Button
        component='label'
        onClick={() => setSelectedUploadMethod(UploadMethods.ExternalURL)}
        style={{
          minWidth: '128px',
        }}
      >
        <Cols>
          <Col extraSmallHorizontalPadding>
            <NoteAddIcon
              style={{
                color: `${publicaColors.blue}`,
              }}
            />
          </Col>
          <Col>
            <Text color={publicaColors.blue}>House Ad URL</Text>
          </Col>
        </Cols>
      </Button>
    );
  };

  const UploadRow = () => {
    return (
      <Button
        component='label'
        onClick={() => setSelectedUploadMethod(UploadMethods.LocalFile)}
        style={{
          minWidth: '128px',
        }}
      >
        <Cols>
          <Col extraSmallHorizontalPadding>
            <BackupIcon
              style={{
                color: `${publicaColors.blue}`,
              }}
            />
          </Col>
          <Col>
            <Text color={publicaColors.blue}>Upload Mediafile</Text>
          </Col>
        </Cols>
        <input
          type='file'
          style={{ display: 'none' }}
          onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
            uploadSSAIHouseAdMediafile(evt, houseAdName);
          }}
        />
      </Button>
    );
  };

  const MenuTitle = () => {
    const buttonColor = getColorTheme('primaryButton');
    return (
      <Cols
        style={{
          alignContent: 'center',
        }}
      >
        <Col>
          <AddIcon
            style={{
              color: buttonColor,
            }}
          />
        </Col>
        <Col>
          <Text color={buttonColor} bold>
            Add New House Ad
          </Text>
        </Col>
      </Cols>
    );
  };

  const createFromAssetURLRow = {
    closeOnClick: true,
    render: CreateFromAssetURLRow,
  };
  const uploadRow = {
    closeOnClick: false,
    render: UploadRow,
  };
  const menuButtonItems = [createFromAssetURLRow, uploadRow];
  return (
    <>
      <Panel>
        <Rows>
          <Row noHorizontalPadding verticalPadding>
            <Text title bold>
              Add House Ads for Fetch On Demand
            </Text>
          </Row>
          <Row noHorizontalPadding>
            <Text small muted>
              House Ads are padded into the ad break when there are not enough ads to fill the ad
              pod entirely. We recommend adding a variety of 5, 10, 30, and 60 second ads to prevent
              black screens from being displayed to the user.
            </Text>
          </Row>
        </Rows>
      </Panel>
      <Panel paper>
        <Rows largePadding>
          <Row>
            <Cols
              style={{
                alignContent: 'center',
              }}
            >
              <Col
                style={{
                  minWidth: '160px',
                }}
              >
                <Text title bold>
                  Media
                </Text>
              </Col>
              <Col>
                <Rows>
                  <Row noVerticalPadding>
                    <PillSwitcher
                      currentText={assignHouseAdOption}
                      pills={houseAdUploadPills}
                      onChange={handleUploadMethodPillChange}
                      customStyle={{ width: '200px' }}
                    />
                  </Row>
                  {assignHouseAdOption === AssignHouseAdOptions.Existing && (
                    <Row>
                      <MaterialSearchDropdownMulti
                        fontColor='black'
                        fontSize='16px'
                        width='256px'
                        menuTitle='Existing Asset'
                        enableMultiSelect={true}
                        list={ssaiHouseAdsDropdownRows}
                        onChange={(list: IMenuList) => {
                          handleUpdateSSAIHouseAdsDropdownAndTableRows(list);
                        }}
                        overrideDefaultSortMode={6}
                        selectAll={true}
                      />
                    </Row>
                  )}
                  {assignHouseAdOption === AssignHouseAdOptions.New && (
                    <Row>
                      <MenuButton
                        forceClosed={false}
                        titleRender={MenuTitle}
                        items={menuButtonItems}
                        minWidth={200}
                        enableDividers={true}
                      />
                    </Row>
                  )}
                </Rows>
              </Col>
            </Cols>
          </Row>
          {assignHouseAdOption === AssignHouseAdOptions.New && (
            <Row>
              <Cols
                style={{
                  alignItems: 'center',
                }}
              >
                <Col
                  style={{
                    minWidth: '176px',
                  }}
                >
                  <Text title bold>
                    House Ad Name
                  </Text>
                </Col>
                <Col>
                  <OutlinedInput
                    value={houseAdName}
                    placeholder='Enter custom name'
                    style={{ minWidth: '450px' }}
                    onChange={(event) => setHouseAdName(event.target.value)}
                  />
                </Col>
              </Cols>
            </Row>
          )}
          {selectedUploadMethod === UploadMethods.ExternalURL && (
            <Row>
              <Cols
                style={{
                  alignItems: 'center',
                }}
              >
                <Col
                  style={{
                    minWidth: '176px',
                  }}
                >
                  <Text title bold>
                    House Ad URL
                  </Text>
                </Col>
                <Col>
                  <OutlinedInput
                    value={externalURL}
                    placeholder='Enter URL'
                    style={{ minWidth: '450px' }}
                    onChange={(event) => setExternalURL(event.target.value)}
                  />
                </Col>
                <Col largeHorizontalPadding>
                  <AddButton />
                </Col>
              </Cols>
            </Row>
          )}
          <Row>
            <ListViewTable
              activeColumnKeys={ssaiHouseAdsCfg.config.activeColumnKeys}
              handleDeleteItem={deleteSSAIHouseAdTableRow}
              page={page}
              rows={displayedRows(ssaiHouseAdsTableRows, page, rowsPerPage)}
              rowsPerPage={rowsPerPage}
              className={''}
              columns={ssaiHouseAdsCfg.table.columns}
              enableActions={ssaiHouseAdsCfg.config.enableActions}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              handleOnChangeStatus={() => null}
              handleOnClickColumnHeader={handleOnClickColumnHeader}
              isExpanded={ssaiHouseAdsCfg.config.isExpanded}
              loadState={loadingState}
              order={ssaiHouseAdsCfg.config.order}
              orderBy={ssaiHouseAdsCfg.config.orderBy}
              totalRowCount={ssaiHouseAdsTableRows.length}
              displayNameWithID={ssaiHouseAdsCfg.config.displayNameWithID}
              isReadAuthorized={ssaiHouseAdsCfg.config.isReadAuthorized}
              isWriteAuthorized={ssaiHouseAdsCfg.config.isWriteAuthorized}
            />
          </Row>
        </Rows>
      </Panel>
      {isInWizard && (
        <Panel>
          <WizardButtons
            onNext={onNext}
            onBack={onBack}
            disableNextButton={ssaiHouseAdsTableRows.length < 1}
          />
        </Panel>
      )}
    </>
  );
};

export default HouseAdsUploadForm;
